import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiClock, FiSave, FiArrowRight, FiArrowLeft, FiCheckCircle, FiLogOut } from "react-icons/fi";
import getApiBaseUrl from "../../utils/api";

import PGCET_CONFIG from "./PgcetmbaConfig";
import pgcetmbaComputerAwareness from "./PgcetmbaComputerAwareness";
import pgcetmbaAnalyticalReasoning from "./PgcetmbaAnalyticalReasoning";
import pgcetmbaQuantitativeAnalysis from "./PgcetmbaQuantitativeAnalysis";
import pgcetmbaEnglishLanguage from "./PgcetmbaEnglishLanguage";
import pgcetmbaGeneralKnowledge from "./PgcetmbaGeneralKnowledge";

const DATA_MAP = {
  computerAwareness: pgcetmbaComputerAwareness,
  analyticalReasoning: pgcetmbaAnalyticalReasoning,
  quantitativeAnalysis: pgcetmbaQuantitativeAnalysis,
  englishLanguage: pgcetmbaEnglishLanguage,
  generalKnowledge: pgcetmbaGeneralKnowledge
};

const SECTIONS = PGCET_CONFIG.sections.map((s) => ({
  ...s,
  questions: DATA_MAP[s.key] || []
}));

const ALL_QUESTIONS = SECTIONS.flatMap((s) => s.questions);

// CSS selector for the actual site header. Adjust this if your header
// uses a different tag/class/id (e.g. ".site-header", "#main-nav").
const SITE_HEADER_SELECTOR = "header";
// Fallback height (px) used before the real header has been measured,
// or if no matching header element is found on the page.
const FALLBACK_HEADER_HEIGHT = 80;

export default function Pgcetmbatest() {
  const navigate = useNavigate();

  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [visited, setVisited] = useState({});
  const [timeLeft, setTimeLeft] = useState(PGCET_CONFIG.durationMinutes * 60);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [focusedQId, setFocusedQId] = useState(null);

  const questionRefs = useRef({});
  const submittedRef = useRef(false);
  const answersRef = useRef({});

  const API_URL = getApiBaseUrl();
  const currentSection = SECTIONS[activeSectionIndex];
  const isLastSection = activeSectionIndex === SECTIONS.length - 1;

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Measure the real site header height at runtime and expose it as a CSS
  // custom property so the fixed top-bar / palette panel always sit flush
  // below it, instead of relying on a hardcoded pixel value that breaks
  // whenever the header's actual height changes.
  useEffect(() => {
    const setHeaderHeightVar = () => {
      const header = document.querySelector(SITE_HEADER_SELECTOR);
      const height = header
        ? header.getBoundingClientRect().height
        : FALLBACK_HEADER_HEIGHT;
      document.documentElement.style.setProperty(
        "--site-header-height",
        `${height}px`
      );
    };

    setHeaderHeightVar();

    // Re-measure on resize (header height can change across breakpoints)
    window.addEventListener("resize", setHeaderHeightVar);

    // Re-measure if the header's own content/size changes after mount
    // (e.g. a logo image loading in, a banner appearing, etc.)
    let resizeObserver;
    const headerEl = document.querySelector(SITE_HEADER_SELECTOR);
    if (headerEl && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(setHeaderHeightVar);
      resizeObserver.observe(headerEl);
    }

    return () => {
      window.removeEventListener("resize", setHeaderHeightVar);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (showResult) return;
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [showResult]);

  // Only reset focus to the section's first question if the currently
  // focused question doesn't belong to this section (e.g. via the palette
  // jumping into a different section). This mirrors the MCA test behaviour.
  useEffect(() => {
    if (currentSection && currentSection.questions.length > 0) {
      const idsInSection = currentSection.questions.map((q) => q.id);
      if (!idsInSection.includes(focusedQId)) {
        setFocusedQId(currentSection.questions[0].id);
      }
    }
  }, [activeSectionIndex]); // eslint-disable-line

  useEffect(() => {
    if (focusedQId !== null && questionRefs.current[focusedQId]) {
      questionRefs.current[focusedQId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusedQId]);

  // Mark whichever question is currently focused as "visited".
  useEffect(() => {
    if (focusedQId === null) return;
    setVisited((prev) => (prev[focusedQId] ? prev : { ...prev, [focusedQId]: true }));
  }, [focusedQId]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleSelect = (qId, letter) => {
    if (showResult) return;
    setAnswers((prev) => ({ ...prev, [qId]: letter }));
  };

  const isAnswered = (id) => answers[id] !== undefined && answers[id] !== "";

  // Status for the question palette: 'current' | 'answered' | 'notAnswered' | 'notVisited'
  const getQuestionStatus = (qId) => {
    if (qId === focusedQId) return "current";
    if (isAnswered(qId)) return "answered";
    if (visited[qId]) return "notAnswered";
    return "notVisited";
  };

  const jumpToQuestion = (sectionIdx, qId) => {
    setActiveSectionIndex(sectionIdx);
    setFocusedQId(qId);
  };

  const paletteCounts = ALL_QUESTIONS.reduce(
    (acc, q) => {
      const status = getQuestionStatus(q.id);
      acc[status] += 1;
      return acc;
    },
    { current: 0, answered: 0, notAnswered: 0, notVisited: 0 }
  );

  const saveTestResult = async (finalScore, breakdown) => {
    try {
      const response = await fetch(`${API_URL}/tests/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          testName: "PGCET MBA Mock Test",
          result: {
            score: finalScore,
            total: PGCET_CONFIG.totalMarks,
            percentage: Math.round((finalScore / PGCET_CONFIG.totalMarks) * 100),
            breakdown
          }
        })
      });

      const data = await response.json();
      console.log("PGCET MBA result saved:", data);

    } catch (err) {
      console.error("Save failed:", err);
    }
  };

  const calculateScore = () => {
    let newScore = 0;
    const breakdown = {};
    const liveAnswers = answersRef.current;

    ALL_QUESTIONS.forEach((q) => {
      const userAns = (liveAnswers[q.id] || "").toUpperCase();
      const correctAns = (q.answer || "").toUpperCase();
      const isCorrect = userAns !== "" && userAns === correctAns;
      if (isCorrect) newScore += 1;
      breakdown[q.id] = { user: liveAnswers[q.id] || null, correct: q.answer, isCorrect };
    });

    setScore(newScore);
    return { newScore, breakdown };
  };

  const handleSubmit = () => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    const { newScore, breakdown } = calculateScore();
    setShowResult(true);
    window.scrollTo(0, 0);
    saveTestResult(newScore, breakdown);
  };

  const goNextSection = () => {
    if (isLastSection) {
      handleSubmit();
    } else {
      setActiveSectionIndex((i) => i + 1);
      window.scrollTo(0, 0);
    }
  };

  const goPrevSection = () => {
    if (activeSectionIndex > 0) {
      setActiveSectionIndex((i) => i - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleExitTest = () => {
    const confirmExit = window.confirm(
      "Are you sure you want to exit? Your progress will not be saved and the timer will reset if you start the test again."
    );
    if (confirmExit) {
      navigate("/");
    }
  };

  // ---------- RESULT SCREEN ----------
  if (showResult) {
    return (
      <div className="pgcet-result-container">
        <style>{`
          .pgcet-result-container {
            min-height: 100vh;
            background: #ffffff;
            display: flex;
            justify-content: center;
            align-items: flex-start;
            padding: 2rem;
            padding-top: 120px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          .result-card {
            width: 100%;
            max-width: 560px;
            background: #ffffff;
            border: 1px solid #bbf7d0;
            border-radius: 20px;
            padding: 3rem;
            text-align: center;
            box-shadow: 0 10px 30px rgba(0,0,0,0.08);
          }
          .result-score {
            font-size: 3rem;
            font-weight: 800;
            color: #16a34a;
            margin: 1rem 0 0.5rem 0;
          }
          .result-sub {
            color: #6b7280;
            margin-bottom: 2rem;
          }
          .primary-btn {
            background: #16a34a;
            color: white;
            border: none;
            padding: 0.9rem 2.2rem;
            border-radius: 10px;
            font-weight: 700;
            cursor: pointer;
          }
        `}</style>
        <div className="result-card">
          <FiCheckCircle style={{ fontSize: "3rem", color: "#19fd91" }} />
          <div className="result-score">{score} / {PGCET_CONFIG.totalMarks}</div>
          <p className="result-sub">
            Your PGCET MBA mock test has been submitted and saved.
          </p>
          <button className="primary-btn" onClick={() => navigate("/profile")}>
            Go to Profile
          </button>
        </div>
      </div>
    );
  }

  // ---------- MAIN TEST SCREEN ----------
  return (
    <div className="pgcet-container">
      <style>{`
        * { box-sizing: border-box; }

        .pgcet-container {
          min-height: 100px;
          background: #ffffff;
          padding: 1.5rem;
          /* Room for the fixed header + fixed top-bar that sit above the
             questions list. Computed from the measured header height plus
             the top-bar's own height (~140px) instead of a bare constant,
             so it stays correct if the header height ever changes. */
          padding-top: calc(var(--site-header-height, 80px) + 140px);
          padding-bottom: 100px;
          padding-right: 356px; /* room for the permanent palette sidebar */
          color: #111827;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .top-bar {
          position: fixed;
          top: var(--site-header-height, 80px);
          left: 0;
          right: 340px;
          z-index: 20;
          background: #ffffff;
          max-width: 900px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #d1fae5;
          padding: 1rem 1.5rem;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .test-title {
          font-size: 1.4rem;
          font-weight: 800;
          color: #166534;
        }

        .section-label {
          font-size: 0.95rem;
          color: #6b7280;
          margin-top: 2px;
        }

        .timer-box {
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: monospace;
          font-size: 1.2rem;
          font-weight: 800;
          padding: 8px 16px;
          border-radius: 12px;
          border: 1px solid #bbf7d0;
          background: #f0fdf4;
          color: #166534;
        }

        .timer-warning {
          background: #fee2e2;
          color: #dc2626;
          border-color: #fca5a5;
        }

        .questions-wrap {
          max-width: 900px;
          margin: 0 auto;
        }

        .question-card {
          background: #ffffff;
          border: 1px solid #d1fae5;
          border-radius: 12px;
          padding: 1.2rem 1.4rem;
          margin-bottom: 1rem;
          scroll-margin-top: 20px;
        }

        .question-card.focused-card {
          border-color: #16a34a;
          background: #f0fdf4;
        }

        .q-top {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          margin-bottom: 0.9rem;
        }

        .q-num {
          flex-shrink: 0;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          background: #dcfce7;
          color: #15803d;
          font-size: 0.8rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .q-text {
          color: #14532d;
          font-size: 1rem;
          line-height: 1.5;
          white-space: pre-line;
        }

        .options-wrap {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          padding-left: 2.5rem;
        }

        .option-row {
          display: flex;
          align-items: center;
          gap: 0.7rem;
          padding: 0.6rem 0.9rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.92rem;
          color: #374151;
          transition: all 0.15s ease;
        }

        .option-row:hover {
          background: #f9fffb;
        }

        .option-row.selected {
          border-color: #16a34a;
          background: #f0fdf4;
          color: #14532d;
          font-weight: 600;
        }

        .option-row.correct-opt {
          border-color: #16a34a;
          background: #dcfce7;
        }

        .option-row.wrong-opt {
          border-color: #ef4444;
          background: #fee2e2;
        }

        .nav-dock {
          position: sticky;
          bottom: 0;
          left: 0;
          right: 0;
          background: #ffffff;
          border-top: 1px solid #d1fae5;
          padding: 0.6rem 1.2rem 0.7rem 1.2rem;
          box-shadow: 0 -4px 20px rgba(0,0,0,0.06);
        }

        .nav-dock-inner {
          max-width: 900px;
          margin: 0 auto;
        }

        .nav-dock-label {
          font-size: 0.8rem;
          font-weight: 700;
          color: #166534;
          margin-bottom: 0.5rem;
        }

        .nav-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 0.25rem;
          margin-bottom: 0.3rem;
          max-height: 40px;
          overflow-y: auto;
        }

        .nav-btn {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          border: 1px solid #d1fae5;
          background: #ffffff;
          color: #15803d;
          font-size: 0.72rem;
          font-weight: 700;
          cursor: pointer;
        }

        .nav-btn.answered {
          background: #dcfce7;
          border-color: #16a34a;
        }

        .nav-btn.focused {
          background: #16a34a;
          color: white;
        }

        .dock-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.8rem;
        }

        .dock-footer-left {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .dock-footer-right {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          flex-wrap: wrap;
        }

        .btn-primary {
          background: #16a34a;
          color: white;
          border: none;
          padding: 0.8rem 1.8rem;
          border-radius: 10px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-secondary {
          background: #ffffff;
          color: #166534;
          border: 1px solid #bbf7d0;
          padding: 0.8rem 1.5rem;
          border-radius: 10px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-secondary:hover {
          background: #f0fdf4;
        }

        .btn-exit {
          background: #ffffff;
          color: #dc2626;
          border: 1px solid #fca5a5;
          padding: 0.6rem 1.2rem;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.85rem;
        }

        .btn-exit:hover {
          background: #fee2e2;
        }

        /* ---------- QUESTION PALETTE (permanent sidebar) ---------- */
        .palette-panel {
          position: fixed;
          top: var(--site-header-height, 80px);
          right: 0;
          bottom: 0;
          width: 340px;
          background: #ffffff;
          border-left: 1px solid #d1fae5;
          box-shadow: -6px 0 20px rgba(0,0,0,0.05);
          display: flex;
          flex-direction: column;
          z-index: 15;
        }

        .palette-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.2rem;
          border-bottom: 1px solid #d1fae5;
        }

        .palette-header h3 {
          margin: 0;
          font-size: 1.05rem;
          color: #166534;
        }

        .palette-legend {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.5rem;
          padding: 0.9rem 1.2rem;
          border-bottom: 1px solid #f0fdf4;
        }

        .palette-legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.75rem;
          color: #4b5563;
        }

        .legend-swatch {
          width: 14px;
          height: 14px;
          border-radius: 4px;
          flex-shrink: 0;
        }

        .palette-body {
          flex: 1;
          overflow-y: auto;
          padding: 0.8rem 1.2rem 1.5rem 1.2rem;
        }

        .palette-section-block {
          margin-bottom: 1.1rem;
        }

        .palette-section-title {
          font-size: 0.8rem;
          font-weight: 800;
          color: #166534;
          margin-bottom: 0.5rem;
        }

        .palette-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 6px;
        }

        .palette-btn {
          width: 100%;
          aspect-ratio: 1 / 1;
          border-radius: 6px;
          font-size: 0.72rem;
          font-weight: 700;
          cursor: pointer;
          border: 1px solid #d1d5db;
          background: #ffffff;
          color: #4b5563;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .palette-btn.answered {
          background: #16a34a;
          border-color: #16a34a;
          color: #ffffff;
        }

        .palette-btn.notAnswered {
          background: #fee2e2;
          border-color: #ef4444;
          color: #b91c1c;
        }

        .palette-btn.notVisited {
          background: #ffffff;
          border-color: #d1d5db;
          color: #6b7280;
        }

        .palette-btn.current {
          background: #f59e0b;
          border-color: #f59e0b;
          color: #ffffff;
          box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.35);
        }

        @media (max-width: 1000px) {
          .pgcet-container { padding-right: 1.5rem; }
          .top-bar { right: 0; max-width: 900px; }
          .palette-panel {
            position: static;
            width: 100%;
            height: 420px;
            border-left: none;
            border-top: 1px solid #d1fae5;
            margin-top: 1rem;
            box-shadow: none;
          }
        }

        @media (max-width: 600px) {
          .pgcet-container { padding-bottom: 260px; padding-top: calc(var(--site-header-height, 80px) + 140px); }
          .options-wrap { padding-left: 0; }
          .top-bar { flex-direction: column; align-items: flex-start; gap: 0.4rem; padding: 0.8rem 1rem; }
          .test-title { font-size: 1.1rem; }
          .timer-box { font-size: 1rem; padding: 6px 12px; }
          .q-text { font-size: 0.92rem; }
          .nav-btn { width: 26px; height: 26px; font-size: 0.68rem; }
        }

      `}</style>

      <div className="top-bar">
        <div>
          <div className="test-title">{PGCET_CONFIG.title}</div>
          <div className="section-label">
            Section {activeSectionIndex + 1} of {SECTIONS.length}: {currentSection.label}
          </div>
        </div>
        <div className={`timer-box ${timeLeft < 300 ? "timer-warning" : ""}`}>
          <FiClock /> {formatTime(timeLeft)}
        </div>
      </div>

      <div className="questions-wrap">
        {currentSection.questions.map((q) => {
          const focused = focusedQId === q.id;
          const selected = answers[q.id];
          return (
            <div
              key={q.id}
              ref={(el) => (questionRefs.current[q.id] = el)}
              className={`question-card ${focused ? "focused-card" : ""}`}
              onClick={() => setFocusedQId(q.id)}
            >
              <div className="q-top">
                <div className="q-num">{q.id}</div>
                <div className="q-text">{q.question}</div>
              </div>

              <div className="options-wrap">
                {q.options.map((opt) => {
                  const letter = opt.trim().charAt(0);
                  const isSelected = selected === letter;
                  let cls = "option-row";
                  if (isSelected) cls += " selected";
                  if (showResult) {
                    if (letter === (q.answer || "").toUpperCase()) cls += " correct-opt";
                    else if (isSelected) cls += " wrong-opt";
                  }
                  return (
                    <div
                      key={opt}
                      className={cls}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(q.id, letter);
                      }}
                    >
                      {opt}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="nav-dock">
        <div className="nav-dock-inner">
          <div className="nav-dock-label">
            {currentSection.label} — Q{currentSection.idRange[0]}–{currentSection.idRange[1]}
          </div>
          <div className="nav-buttons">
            {currentSection.questions.map((q) => {
              const answered = isAnswered(q.id);
              const focused = focusedQId === q.id;
              let cls = "nav-btn";
              if (focused) cls += " focused";
              else if (answered) cls += " answered";
              return (
                <button key={q.id} className={cls} onClick={() => setFocusedQId(q.id)}>
                  {q.id}
                </button>
              );
            })}
          </div>
          <div className="dock-footer">
            <div className="dock-footer-left">
              <button className="btn-exit" onClick={handleExitTest}>
                <FiLogOut /> Exit
              </button>
              <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                {Object.keys(answers).length} / {PGCET_CONFIG.totalQuestions} answered
              </span>
            </div>
            <div className="dock-footer-right">
              {activeSectionIndex > 0 && (
                <button className="btn-secondary" onClick={goPrevSection}>
                  <FiArrowLeft /> Previous Section
                </button>
              )}
              <button className="btn-primary" onClick={goNextSection}>
                {isLastSection ? (
                  <>Submit Test <FiSave /></>
                ) : (
                  <>Next Section <FiArrowRight /></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="palette-panel">
        <div className="palette-header">
          <h3>Question Palette</h3>
        </div>

        <div className="palette-legend">
          <div className="palette-legend-item">
            <span className="legend-swatch" style={{ background: "#16a34a" }} />
            Answered ({paletteCounts.answered})
          </div>
          <div className="palette-legend-item">
            <span className="legend-swatch" style={{ background: "#fee2e2", border: "1px solid #ef4444" }} />
            Not answered ({paletteCounts.notAnswered})
          </div>
          <div className="palette-legend-item">
            <span className="legend-swatch" style={{ background: "#ffffff", border: "1px solid #d1d5db" }} />
            Not visited ({paletteCounts.notVisited})
          </div>
          <div className="palette-legend-item">
            <span className="legend-swatch" style={{ background: "#f59e0b" }} />
            Current
          </div>
        </div>

        <div className="palette-body">
          {SECTIONS.map((section, sIdx) => (
            <div className="palette-section-block" key={section.key}>
              <div className="palette-section-title">
                {sIdx + 1}. {section.label}
              </div>
              <div className="palette-grid">
                {section.questions.map((q) => {
                  const status = getQuestionStatus(q.id);
                  return (
                    <button
                      key={q.id}
                      className={`palette-btn ${status}`}
                      onClick={() => jumpToQuestion(sIdx, q.id)}
                      title={`Question ${q.id}`}
                    >
                      {q.id}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}